<template>
  <div class="space-y-6">
    <div class="bg-white rounded-lg shadow p-6">
      <div class="flex justify-between items-center mb-6">
        <h2 class="text-xl font-bold">Doctors</h2>
        <button @click="openForm" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">
          ➕ New Doctor
        </button>
      </div>

      <div v-if="masterDataStore.loading" class="text-center py-12">
        <div class="inline-block spinner w-8 h-8"></div>
      </div>
      <div v-else-if="masterDataStore.doctors.length === 0" class="text-center py-12 text-gray-500">
        No doctors found
      </div>
      <div v-else class="overflow-x-auto">
        <table class="w-full">
          <thead class="bg-gray-100 border-b">
            <tr>
              <th class="px-6 py-3 text-left text-sm font-semibold">ID</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Name</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Email</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">District</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Status</th>
              <th class="px-6 py-3 text-left text-sm font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y">
            <tr v-for="doctor in masterDataStore.doctors" :key="doctor.id" class="hover:bg-gray-50">
              <td class="px-6 py-4 text-sm text-gray-500">{{ doctor.id }}</td>
              <td class="px-6 py-4 text-sm font-medium">{{ doctor.full_name || doctor.name }}</td>
              <td class="px-6 py-4 text-sm text-gray-600">{{ doctor.email }}</td>
              <td class="px-6 py-4 text-sm">{{ getDistrictName(doctor.district_id) }}</td>
              <td class="px-6 py-4 text-sm">
                <span :class="[
                  'px-2 py-1 rounded text-xs',
                  String(doctor.is_active) === '1' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                ]">
                  {{ String(doctor.is_active) === '1' ? 'Active' : 'Inactive' }}
                </span>
              </td>
              <td class="px-6 py-4 text-sm space-x-2">
                <button @click="editDoctor(doctor)" class="text-blue-600 hover:text-blue-800">Edit</button>
                <button @click="deleteDoctorFunc(doctor.id)" class="text-red-600 hover:text-red-800">Delete</button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>

    <!-- Form Modal -->
    <div v-show="showForm" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div class="bg-white rounded-lg shadow-xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto z-50 relative">
        <h3 class="text-lg font-bold mb-4">{{ editingDoctor ? 'Edit Doctor' : 'New Doctor' }}</h3>
        <form @submit.prevent="submitForm" class="space-y-4">
          <!-- Login Credentials Section -->
          <div class="pb-3 border-b">
            <h4 class="text-sm font-semibold text-gray-700 mb-3">Login Credentials</h4>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-1">Email (Username) <span class="text-red-500">*</span></label>
                <input 
                  v-model="formData.email" 
                  type="email" 
                  placeholder="doctor@example.com" 
                  class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" 
                  :required="!editingDoctor" 
                  :disabled="editingDoctor !== null"
                />
                <p v-if="editingDoctor" class="text-xs text-gray-500 mt-1">Email cannot be changed</p>
              </div>
              <div v-if="!editingDoctor">
                <label class="block text-sm font-medium mb-1">Password <span class="text-red-500">*</span></label>
                <input 
                  v-model="formData.password" 
                  type="password" 
                  placeholder="Minimum 8 characters" 
                  class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" 
                  required 
                  minlength="8"
                />
              </div>
              <div v-if="!editingDoctor">
                <label class="block text-sm font-medium mb-1">Confirm Password <span class="text-red-500">*</span></label>
                <input 
                  v-model="formData.confirmPassword" 
                  type="password" 
                  placeholder="Re-enter password" 
                  class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" 
                  required 
                  minlength="8"
                />
              </div>
              <div v-if="editingDoctor" class="bg-blue-50 p-3 rounded">
                <p class="text-sm text-blue-800">💡 To reset password, use the "Reset Password" option in actions menu</p>
              </div>
            </div>
          </div>

          <!-- Doctor Information Section -->
          <div>
            <h4 class="text-sm font-semibold text-gray-700 mb-3">Doctor Information</h4>
            <div class="space-y-3">
              <div>
                <label class="block text-sm font-medium mb-1">Full Name <span class="text-red-500">*</span></label>
                <input v-model="formData.full_name" placeholder="Dr. John Smith" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" required />
              </div>
              <div>
                <label class="block text-sm font-medium mb-1">District <span class="text-red-500">*</span></label>
                <select v-model="formData.district_id" class="w-full px-4 py-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-600" required>
                  <option value="">Select District</option>
                  <option v-for="district in masterDataStore.districts" :key="district.id" :value="district.id">
                    {{ district.name }}
                  </option>
                </select>
              </div>
            </div>
          </div>

          <div class="flex items-center">
            <input v-model="formData.is_active" type="checkbox" id="doctor-active" class="w-4 h-4" />
            <label for="doctor-active" class="ml-2 text-sm">Active</label>
          </div>
          <div class="flex justify-end gap-2 pt-4 border-t">
            <button type="button" @click="closeForm" class="px-4 py-2 border rounded hover:bg-gray-50">Cancel</button>
            <button type="submit" class="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700">{{ editingDoctor ? 'Update' : 'Create' }}</button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import Swal from 'sweetalert2'
import { useMasterDataStore } from '@/stores/masterDataStore'

const masterDataStore = useMasterDataStore()
const showForm = ref(false)
const editingDoctor = ref(null)
const formData = ref({ 
  email: '', 
  password: '', 
  confirmPassword: '',
  full_name: '', 
  district_id: '', 
  is_active: true 
})

const getDistrictName = (districtId) => {
  const d = masterDataStore.districts.find(d => String(d.id) === String(districtId))
  return d ? d.name : 'N/A'
}

const submitForm = async () => {
  try {
    // Validate required fields for new doctor
    if (!editingDoctor.value) {
      if (!formData.value.email || !formData.value.password || !formData.value.full_name || !formData.value.district_id) {
        Swal.fire({
          icon: 'warning',
          title: 'Missing Fields',
          text: 'Email, Password, Full Name, and District are required',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      // Validate password match
      if (formData.value.password !== formData.value.confirmPassword) {
        Swal.fire({
          icon: 'warning',
          title: 'Password Mismatch',
          text: 'Password and Confirm Password do not match',
          confirmButtonColor: '#3085d6'
        })
        return
      }

      // Validate password length
      if (formData.value.password.length < 8) {
        Swal.fire({
          icon: 'warning',
          title: 'Weak Password',
          text: 'Password must be at least 8 characters',
          confirmButtonColor: '#3085d6'
        })
        return
      }
    }

    const data = {
      full_name: formData.value.full_name,
      district_id: Number(formData.value.district_id),
      is_active: formData.value.is_active ? 1 : 0
    }

    // Add email and password only for new doctors
    if (!editingDoctor.value) {
      data.email = formData.value.email
      data.password = formData.value.password
    }

    if (editingDoctor.value) {
      await masterDataStore.updateDoctor(editingDoctor.value.id, data)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Doctor updated successfully',
        confirmButtonColor: '#3085d6'
      })
    } else {
      await masterDataStore.createDoctor(data)
      Swal.fire({
        icon: 'success',
        title: 'Success',
        text: 'Doctor created successfully. Login credentials sent.',
        confirmButtonColor: '#3085d6'
      })
    }
    closeForm()
    await masterDataStore.getDoctors()
  } catch (error) {
    console.error('Form submission error:', error)
    Swal.fire({
      icon: 'error',
      title: 'Error',
      text: error.message || 'Failed to save doctor',
      confirmButtonColor: '#3085d6'
    })
  }
}

const closeForm = () => {
  showForm.value = false
  editingDoctor.value = null
  formData.value = { 
    email: '', 
    password: '', 
    confirmPassword: '',
    full_name: '', 
    district_id: '', 
    is_active: true 
  }
}

const openForm = () => {
  console.log('Opening doctor form...')
  showForm.value = true
}

const editDoctor = (doctor) => {
  editingDoctor.value = doctor
  formData.value = { ...doctor }
  showForm.value = true
}

const deleteDoctorFunc = async (id) => {
  Swal.fire({
    title: 'Confirm Delete',
    text: 'Are you sure you want to delete this doctor?',
    icon: 'warning',
    showCancelButton: true,
    confirmButtonColor: '#d33',
    cancelButtonColor: '#3085d6',
    confirmButtonText: 'Yes, delete it',
    cancelButtonText: 'Cancel'
  }).then(async (result) => {
    if (result.isConfirmed) {
      await masterDataStore.deleteDoctor(id)
      Swal.fire({
        icon: 'success',
        title: 'Deleted',
        text: 'Doctor deleted successfully',
        confirmButtonColor: '#3085d6'
      })
    }
  })
}

onMounted(async () => {
  await masterDataStore.getDoctors()
  await masterDataStore.getDistricts()
})
</script>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>

